import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { aiRouteErrorCode } from "../../lib/aiRouteError.js";
import ReactMarkdown from "react-markdown";
import type { OperationCanvasNodeData, OperationCanvasNodeType, NodeRunStatus } from "../OperationCanvasNode.js";
import type { QueryResultsPayload } from "../QueryResultsGrid.js";
import { TanTableViz } from "../TanTableViz.js";
import { apiFetch, ApiRequestError } from "../../lib/api.js";
import { useAiChatThread } from "../../hooks/useAiChatThread.js";
import { ChatComposer, ChatThinkingIndicator, ChatErrorBanner } from "../aiChat/index.js";
import { isAiUsageLimitError } from "../../lib/aiRateLimit.js";
import { opAiChatMarkdownComponents } from "../../lib/opAiChatMarkdown.js";
import { computeStructuredSnapshotDrift, type StructuredSnapshotDrift } from "../../lib/structuredSnapshotDrift.js";
import { queryResultsToTanTableModel } from "../../lib/queryResultsToTanTable.js";
import { useToast } from "../Toast.js";
import { NodeMetadataFields, type NodeMetadata } from "./nodeInspectorWidgets.js";
import { formatUtcISOInTz } from "../../lib/cronDisplay.js";
import { InspectorTabs } from "../inspector/InspectorTabs.js";
import type { InspectorSlot } from "../inspector/inspectorModel.js";

/** One structured output field for the AI Provider tabular/JSON port. */
export type AiProviderOutputColumn = { name: string; type: string; description?: string };

/** Persisted config for the AI Provider (research) node — mirrors hub spec shape. */
export type AiProviderNodeConfig = {
  mode?: "authoring" | "production";
  prompt?: string;
  /** Column schema for structured output; concatenated into the execution prompt on the hub. */
  output_schema?: AiProviderOutputColumn[];
  content_format?: "markdown" | "svg" | "html";
  search_config?: {
    web_search_enabled?: boolean;
    max_search_passes?: 1 | 2 | 3;
    max_results_per_search?: number;
  };
  model_tuning?: { temperature?: number; max_output_tokens?: number };
  citations_required?: boolean;
  allow_null_when_unsupported?: boolean;
  locked_at?: string;
  execution_model_id?: string;
  authoring_model_id?: string;
  /** When false, model is not asked for tabular `records`; schema not required for Execute. Default true when omitted. */
  enable_structured?: boolean;
  /** When false, model is not asked for narrative `content`. Default true when omitted. */
  enable_unstructured?: boolean;
};


const SCHEMA_COLUMN_TYPES = ["string", "number", "date", "boolean"] as const;
type SchemaColumnType = (typeof SCHEMA_COLUMN_TYPES)[number];

const SCHEMA_COL_NAME_PCT = { min: 10, max: 52 };
const SCHEMA_COL_TYPE_PX = { min: 52, max: 200 };

function coerceSchemaType(s: string): SchemaColumnType {
  return (SCHEMA_COLUMN_TYPES as readonly string[]).includes(s) ? (s as SchemaColumnType) : "string";
}

function normalizeOutputSchema(input: unknown): AiProviderOutputColumn[] | null {
  if (!Array.isArray(input) || input.length === 0) return null;
  const out: AiProviderOutputColumn[] = [];
  for (const item of input) {
    if (!item || typeof item !== "object" || Array.isArray(item)) return null;
    const o = item as Record<string, unknown>;
    if (typeof o.name !== "string" || typeof o.type !== "string") return null;
    const row: AiProviderOutputColumn = { name: o.name, type: o.type };
    if (typeof o.description === "string" && o.description.trim()) row.description = o.description.trim();
    out.push(row);
  }
  return out;
}

export type TenantAiModelOption = { model_id: string; status: string };

type ChatLine = {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  created_at: string;
  suggestedPrompt?: string | null;
  suggestedSchema?: unknown[] | null;
  suggestedSettings?: { suggested_execution_model_id?: string | null } | null;
};

type RunSummary = {
  id: string;
  is_test: boolean;
  status: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  model_used: string | null;
  duration_ms: number | null;
  error_message: string | null;
};

type AiProviderRunDetail = RunSummary & {
  structured_snapshot: unknown | null;
  content_snapshot: unknown | null;
  citations_snapshot: unknown | null;
};

type HistoryResultTab = "structured" | "unstructured" | "citations" | "debug";

type AiProviderExecuteResponse = {
  run: AiProviderRunDetail;
  debug_raw_text?: string | null;
  drift?: StructuredSnapshotDrift;
  /** ADR-0059: present when Hub substituted a web-capable model for this call. */
  substituted_from_model?: string | null;
};

function asQueryResultsPayload(raw: unknown): QueryResultsPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (!Array.isArray(o.columns) || !Array.isArray(o.rows)) return null;
  if (
    !o.columns.every(
      (c) => c && typeof c === "object" && typeof (c as { name?: string }).name === "string"
    )
  ) {
    return null;
  }
  return raw as QueryResultsPayload;
}

function citationsToQueryPayload(raw: unknown): QueryResultsPayload | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const columns = [
    { name: "ref_id", type: "string" },
    { name: "url", type: "string" },
    { name: "title", type: "string" },
    { name: "snippet", type: "string" },
    { name: "accessed_at", type: "string" }
  ];
  const rows = raw.map((item) => {
    if (!item || typeof item !== "object") {
      return ["", "", "", "", ""];
    }
    const o = item as Record<string, unknown>;
    return [
      String(o.ref_id ?? ""),
      String(o.url ?? ""),
      String(o.title ?? ""),
      String(o.snippet ?? ""),
      String(o.accessed_at ?? "")
    ];
  });
  return { columns, rows, rowCount: rows.length, durationMs: 0 };
}

function parseContentSnapshot(raw: unknown): {
  contentType: "markdown" | "svg" | "html";
  body: string;
  title?: string;
} | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const body = typeof o.body === "string" ? o.body : "";
  const ct = o.contentType === "svg" || o.contentType === "html" ? o.contentType : "markdown";
  return {
    contentType: ct,
    body,
    title: typeof o.title === "string" ? o.title : undefined
  };
}

type ChatSegment = { kind: "text"; text: string } | { kind: "code"; text: string; lang?: string };

/** Split assistant markdown on ``` fenced blocks; remainder is conversational prose. */
function splitFencedBlocks(raw: string): ChatSegment[] {
  const re = /```([\w-]*)\s*\n?([\s\S]*?)```/g;
  const out: ChatSegment[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    if (m.index > last) {
      const t = raw.slice(last, m.index);
      if (t.trim()) out.push({ kind: "text", text: t });
    }
    out.push({ kind: "code", text: (m[2] ?? "").replace(/\n$/, ""), lang: m[1] ? m[1] : undefined });
    last = m.index + m[0].length;
  }
  const tail = raw.slice(last);
  if (tail.trim()) out.push({ kind: "text", text: tail });
  return out.length > 0 ? out : [{ kind: "text", text: raw }];
}

async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  if (!ok) throw new Error("copy failed");
}

function ClipboardIcon() {
  return (
    <svg className="opAiChatCopyIcon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
      <path
        d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  );
}

function CopyableCodeBlock({ label, text, onCopy }: { label: string; text: string; onCopy: () => void }) {
  const copyLabel = `Copy ${label}`;
  return (
    <div className="opAiChatCodeBlock">
      <div className="opAiChatCodeToolbar">
        <span className="opAiChatCodeLabel">{label}</span>
        <button
          type="button"
          className="btn btnSm btnTertiary opAiChatCopyIconBtn"
          aria-label={copyLabel}
          title={copyLabel}
          onClick={() => onCopy()}
        >
          <ClipboardIcon />
        </button>
      </div>
      <textarea className="opAiChatCodeTextarea" readOnly value={text} rows={Math.min(12, Math.max(3, text.split("\n").length))} />
    </div>
  );
}

function AssistantMessageBody({ text }: { text: string }) {
  const toast = useToast();
  const segments = useMemo(() => splitFencedBlocks(text), [text]);

  async function handleCopy(block: string) {
    try {
      await copyToClipboard(block);
      toast.push({ kind: "success", message: "Copied" });
    } catch {
      toast.push({ kind: "error", message: "Could not copy." });
    }
  }

  return (
    <div style={{ display: "grid", gap: 6 }}>
      {segments.map((seg, i) => {
        if (seg.kind === "text") {
          return (
            <div key={i} className="opAiChatProse">
              <ReactMarkdown components={opAiChatMarkdownComponents}>{seg.text}</ReactMarkdown>
            </div>
          );
        }
        const lang = seg.lang?.trim();
        const label = lang ? lang : "Copyable";
        return <CopyableCodeBlock key={i} label={label} text={seg.text} onCopy={() => void handleCopy(seg.text)} />;
      })}
    </div>
  );
}

function formatAppliedSuggestionParts(parts: string[]): string {
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0]!;
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

function AssistantTurn({
  conversationText,
  suggestedPrompt,
  suggestedSchema,
  suggestedExecutionModelId,
  tenantAiModelIds,
  onApplyProductionSuggestions,
  mode
}: {
  conversationText: string;
  suggestedPrompt?: string | null;
  suggestedSchema?: unknown[] | null;
  suggestedExecutionModelId?: string | null;
  tenantAiModelIds: Set<string>;
  onApplyProductionSuggestions?: (payload: {
    prompt: string | null;
    schema: AiProviderOutputColumn[] | null;
    executionModelId: string | null;
  }) => void;
  mode: "authoring" | "production";
}) {
  const toast = useToast();

  async function copyBlock(block: string) {
    try {
      await copyToClipboard(block);
      toast.push({ kind: "success", message: "Copied" });
    } catch {
      toast.push({ kind: "error", message: "Could not copy." });
    }
  }

  const sp = suggestedPrompt?.trim() ?? "";
  const schemaNormalized =
    suggestedSchema !== undefined && suggestedSchema !== null ? normalizeOutputSchema(suggestedSchema) : null;
  const schemaForApply =
    schemaNormalized && schemaNormalized.length > 0 ? schemaNormalized : null;
  const execId = suggestedExecutionModelId?.trim() ?? "";
  const execOk = Boolean(execId && tenantAiModelIds.has(execId));
  const hasSuggestionArtifacts =
    Boolean(sp) || schemaForApply !== null || execOk || Boolean(execId);
  const canApplySuggestions = Boolean(sp) || schemaForApply !== null || execOk;

  return (
    <div style={{ display: "grid", gap: 0 }}>
      <AssistantMessageBody text={conversationText} />
      {hasSuggestionArtifacts ? (
        <div className="opAiChatSuggestedStack">
          {sp ? <CopyableCodeBlock label="Production prompt" text={sp} onCopy={() => void copyBlock(sp)} /> : null}
          {execOk ? (
            <div className="muted" style={{ fontSize: 12 }}>
              Suggested execution model: <span className="mono">{execId}</span>
            </div>
          ) : execId ? (
            <div className="muted" style={{ fontSize: 12 }}>
              Suggested model &quot;{execId}&quot; is not in this tenant&apos;s model list.
            </div>
          ) : null}
          {mode === "authoring" && onApplyProductionSuggestions && canApplySuggestions ? (
            <div style={{ marginTop: 6 }}>
              <button
                type="button"
                className="btn btnSm"
                onClick={() =>
                  onApplyProductionSuggestions({
                    prompt: sp.length > 0 ? sp : null,
                    schema: schemaForApply,
                    executionModelId: execOk ? execId : null
                  })
                }
              >
                Apply to production configuration
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function OperationInspectorAiProvider({
  operationId,
  selectedNode,
  setNodeData,
  queueAutoSave,
  tenantAiModels,
  accountDefaultAiModelId,
  aiChatBlocked,
  onAiChatRateLimited,
  onNodeRunStatusChange,
  executingNodeIds,
  onAuthoringChatBusyChange,
  contextInputs,
  operationMeta
}: {
  operationId: string;
  selectedNode: OperationCanvasNodeType;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  /** Debounced silent save from OperationEditor — call after any persisted node/config change. */
  queueAutoSave?: (delayMs?: number) => void;
  tenantAiModels: TenantAiModelOption[];
  /** Resolved account/workspace default (user pref → hub default → first model); shown when node has no authoring override. */
  accountDefaultAiModelId: string;
  aiChatBlocked: boolean;
  onAiChatRateLimited: () => void;
  onNodeRunStatusChange: (nodeId: string, status: NodeRunStatus) => void;
  /** Set of node IDs currently executing (tracked by parent for cross-node awareness). */
  executingNodeIds: ReadonlySet<string>;
  /** Palette/cross-surface busy for authoring chat without mutating node runStatus (preserves success badge). */
  onAuthoringChatBusyChange?: (nodeId: string, busy: boolean) => void;
  contextInputs: { dataIn: boolean; contextIn: boolean };
  operationMeta: NodeMetadata;
}) {
  const toast = useToast();
  // ADR-0129 — controlled spine slot (configure-first default). Kept in local
  // state so the execute flow can jump to Runs. Config + Schema share the Setup
  // slot via `setupSubTab`.
  const [activeSlot, setActiveSlot] = useState<InspectorSlot>("setup");
  const [setupSubTab, setSetupSubTab] = useState<"config" | "schema">("config");
  const cfg = (selectedNode.data.config ?? {}) as AiProviderNodeConfig;
  const mode = cfg.mode ?? "authoring";
  const persistedPrompt = cfg.prompt ?? "";
  const webSearchEnabled = cfg.search_config?.web_search_enabled !== false;

  const [draftPrompt, setDraftPrompt] = useState(persistedPrompt);
  const {
    sessionId,
    setSessionId,
    messages: chatMessages,
    setMessages: setChatMessages,
    replyPending,
    refresh: refreshChat,
    error: chatThreadError,
    resetChat,
    cancelChat,
  } = useAiChatThread<ChatLine>({
    threadKey: `${operationId}/${selectedNode.id}/ai-provider-authoring`,
    loadLatest: useCallback(async () => {
      const data = await apiFetch<{
        session: { id: string; created_at?: string };
        messages: Array<{
          id: string;
          role: "user" | "assistant" | "system";
          content: string;
          created_at?: string;
          suggested_prompt?: string | null;
          suggested_schema?: unknown[] | null;
          suggested_settings?: { suggested_execution_model_id?: string | null } | null;
        }>;
      }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/ai-provider/authoring/sessions/latest`
      );
      const out: ChatLine[] = [];
      for (const m of data.messages) {
        if (m.role === "user") {
          out.push({ id: m.id, role: "user", content: m.content, created_at: m.created_at ?? "" });
        } else if (m.role === "assistant") {
          out.push({
            id: m.id,
            role: "assistant",
            content: m.content,
            created_at: m.created_at ?? "",
            suggestedPrompt: m.suggested_prompt ?? null,
            suggestedSchema: Array.isArray(m.suggested_schema) ? m.suggested_schema : null,
            suggestedSettings:
              m.suggested_settings && typeof m.suggested_settings === "object"
                ? (m.suggested_settings as { suggested_execution_model_id?: string | null })
                : null,
          });
        }
      }
      return { sessionId: data.session.id, messages: out };
    }, [operationId, selectedNode.id]),
    createNewSession: useCallback(async () => {
      const data = await apiFetch<{ session: { id: string } }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/ai-provider/authoring/sessions`,
        { method: "POST", body: {} }
      );
      return { sessionId: data.session.id };
    }, [operationId, selectedNode.id]),
    cancelChat: useCallback(async (sid: string) => {
      await apiFetch(
        `/api/tenant/ai/sessions/${encodeURIComponent(sid)}/abort`,
        { method: "POST", body: {} }
      );
    }, []),
  });
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  // ADR-0207 §3.4: the hub's error code, for the admin's link under an AI-route refusal.
  const [chatErrorCode, setChatErrorCode] = useState<string | null>(null);
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [tenantTimezone, setTenantTimezone] = useState<string>("UTC");
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [selectedRunDetail, setSelectedRunDetail] = useState<AiProviderRunDetail | null>(null);
  const [runDetailLoading, setRunDetailLoading] = useState(false);
  const [historyResultTab, setHistoryResultTab] = useState<HistoryResultTab>("structured");
  /** Session-only: raw model text from the latest Execute (not persisted). */
  const [lastExecuteDebugRaw, setLastExecuteDebugRaw] = useState<string | null>(null);
  const [lastExecuteDebugRunId, setLastExecuteDebugRunId] = useState<string | null>(null);
  const [lastExecuteDrift, setLastExecuteDrift] = useState<StructuredSnapshotDrift | null>(null);
  const [lastExecuteDriftRunId, setLastExecuteDriftRunId] = useState<string | null>(null);
  /** ADR-0059: set from execute response when Hub substituted a web-capable model. Null otherwise. */
  const [lastExecuteSubstitutedFromModel, setLastExecuteSubstitutedFromModel] = useState<string | null>(null);
  const [lastExecuteSubstitutedRunId, setLastExecuteSubstitutedRunId] = useState<string | null>(null);
  const [olderRunDetail, setOlderRunDetail] = useState<AiProviderRunDetail | null>(null);
  const [olderRunDetailLoading, setOlderRunDetailLoading] = useState(false);
  const executeBusy = executingNodeIds.has(selectedNode.id);
  const [schemaRows, setSchemaRows] = useState<AiProviderOutputColumn[]>([]);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [schemaColWidths, setSchemaColWidths] = useState({ namePct: 22, typePx: 72 });

  const chatScrollRef = useRef<HTMLDivElement>(null);
  // Composer ref + auto-resize now lives inside <ChatComposer />.
  const schemaTableRef = useRef<HTMLTableElement>(null);
  const draftPromptRef = useRef(persistedPrompt);
  draftPromptRef.current = draftPrompt;
  const currentNodeIdRef = useRef(selectedNode.id);
  currentNodeIdRef.current = selectedNode.id;

  const tenantAiModelIds = useMemo(() => new Set(tenantAiModels.map((m) => m.model_id)), [tenantAiModels]);

  useEffect(() => {
    setLastExecuteDebugRaw(null);
    setLastExecuteDebugRunId(null);
    setLastExecuteDrift(null);
    setLastExecuteDriftRunId(null);
    setLastExecuteSubstitutedFromModel(null);
    setLastExecuteSubstitutedRunId(null);
    setOlderRunDetail(null);
  }, [operationId, selectedNode.id]);

  /** Sync draft from persisted; on cleanup flush dirty draft to the node this effect was for (blur, tab change, node change, unmount). */
  useEffect(() => {
    const nodeId = selectedNode.id;
    const persistedSnapshot = persistedPrompt;
    const modeSnapshot = mode;

    setDraftPrompt(persistedSnapshot);

    return () => {
      if (modeSnapshot === "production") return;
      const d = draftPromptRef.current;
      if (d === persistedSnapshot) return;
      setNodeData(nodeId, (data) => ({
        ...data,
        config: {
          ...(data.config as Record<string, unknown>),
          prompt: d
        }
      }));
      queueAutoSave?.();
    };
  }, [selectedNode.id, persistedPrompt, mode, setNodeData, queueAutoSave]);

  useEffect(() => {
    setSchemaColWidths({ namePct: 22, typePx: 72 });
  }, [selectedNode.id]);

  function patchConfig(patch: Record<string, unknown>) {
    setNodeData(selectedNode.id, (data) => ({
      ...data,
      config: {
        ...(data.config as Record<string, unknown>),
        ...patch
      }
    }));
    queueAutoSave?.();
  }

  function patchSearchConfig(patch: Partial<NonNullable<AiProviderNodeConfig["search_config"]>>) {
    const cur = (cfg.search_config ?? {}) as Record<string, unknown>;
    patchConfig({ search_config: { ...cur, ...patch } });
  }

  function patchModelTuning(patch: Partial<NonNullable<AiProviderNodeConfig["model_tuning"]>>) {
    const cur = (cfg.model_tuning ?? {}) as Record<string, unknown>;
    patchConfig({ model_tuning: { ...cur, ...patch } });
  }

  // Hook owns session lifecycle. Post-send and post-apply, call `refreshChat`
  // to pull the server's authoritative state with artifact extras.

  const loadRuns = useCallback(async () => {
    if (!operationId) return;
    setRunsLoading(true);
    setRunsError(null);
    try {
      const data = await apiFetch<{ runs: RunSummary[]; tenant_timezone?: string }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/ai-provider/runs?limit=50`
      );
      setRuns(data.runs ?? []);
      if (data.tenant_timezone) setTenantTimezone(data.tenant_timezone);
    } catch (e) {
      setRunsError(e instanceof Error ? e.message : String(e));
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, [operationId, selectedNode.id]);

  const olderProductionRunId = useMemo(() => {
    if (!selectedRunId || runs.length === 0) return null;
    const idx = runs.findIndex((r) => r.id === selectedRunId);
    if (idx < 0) return null;
    for (let j = idx + 1; j < runs.length; j++) {
      const r = runs[j];
      if (r.status === "success" && !r.is_test) return r.id;
    }
    return null;
  }, [runs, selectedRunId]);

  // Load runs on mount (and node change) so the Runs slot + the Config tab's
  // "Locked …" timestamp are populated without a tab-open trigger. The execute
  // flow calls loadRuns() again and jumps the spine to Runs.
  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    if (runs.length === 0) {
      setSelectedRunId(null);
      setSelectedRunDetail(null);
      return;
    }
    setSelectedRunId((prev) => {
      if (prev && runs.some((r) => r.id === prev)) return prev;
      return runs[0]!.id;
    });
  }, [runs]);

  useEffect(() => {
    if (!operationId || !selectedRunId) {
      setSelectedRunDetail(null);
      return;
    }
    let cancelled = false;
    setRunDetailLoading(true);
    void (async () => {
      try {
        const data = await apiFetch<{ run: AiProviderRunDetail }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/ai-provider/runs/${encodeURIComponent(selectedRunId)}`
        );
        if (!cancelled) setSelectedRunDetail(data.run);
      } catch {
        if (!cancelled) setSelectedRunDetail(null);
      } finally {
        if (!cancelled) setRunDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [operationId, selectedNode.id, selectedRunId]);

  useEffect(() => {
    if (!operationId || !olderProductionRunId) {
      setOlderRunDetail(null);
      return;
    }
    let cancelled = false;
    setOlderRunDetailLoading(true);
    void (async () => {
      try {
        const data = await apiFetch<{ run: AiProviderRunDetail }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/ai-provider/runs/${encodeURIComponent(olderProductionRunId)}`
        );
        if (!cancelled) setOlderRunDetail(data.run);
      } catch {
        if (!cancelled) setOlderRunDetail(null);
      } finally {
        if (!cancelled) setOlderRunDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [operationId, selectedNode.id, olderProductionRunId]);

  const driftFromHistoryCompare = useMemo(() => {
    if (!selectedRunDetail) return null;
    return computeStructuredSnapshotDrift(
      asQueryResultsPayload(olderRunDetail?.structured_snapshot),
      asQueryResultsPayload(selectedRunDetail.structured_snapshot)
    );
  }, [selectedRunDetail, olderRunDetail]);

  const activeStructuredDrift = useMemo(() => {
    if (selectedRunId && lastExecuteDriftRunId === selectedRunId && lastExecuteDrift) return lastExecuteDrift;
    return driftFromHistoryCompare;
  }, [selectedRunId, lastExecuteDriftRunId, lastExecuteDrift, driftFromHistoryCompare]);

  const structuredRunTanModel = useMemo(() => {
    const payload = selectedRunDetail ? asQueryResultsPayload(selectedRunDetail.structured_snapshot) : null;
    if (!payload || payload.columns.length === 0) return null;
    return queryResultsToTanTableModel(payload);
  }, [selectedRunDetail]);

  const citationsRunTanModel = useMemo(() => {
    const payload = selectedRunDetail ? citationsToQueryPayload(selectedRunDetail.citations_snapshot) : null;
    if (!payload || payload.rowCount === 0) return null;
    return queryResultsToTanTableModel(payload);
  }, [selectedRunDetail]);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [chatMessages]);

  async function ensureAuthoringSession(): Promise<string> {
    if (sessionId) return sessionId;
    const data = await apiFetch<{ session: { id: string } }>(
      `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/ai-provider/authoring/sessions`,
      { method: "POST", body: {} }
    );
    setSessionId(data.session.id);
    return data.session.id;
  }

  async function resetAuthoringChat() {
    if (!operationId || chatSending) return;
    setChatSending(true);
    onAuthoringChatBusyChange?.(selectedNode.id, true);
    setChatError(null); setChatErrorCode(null);
    try {
      await apiFetch(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/ai-provider/authoring/sessions`,
        { method: "POST", body: {} }
      );
      await refreshChat();
      toast.push({ kind: "success", message: "Started a new chat session." });
    } catch (e) {
      setChatError(e instanceof Error ? e.message : String(e)); setChatErrorCode(aiRouteErrorCode(e));
    } finally {
      onAuthoringChatBusyChange?.(selectedNode.id, false);
      setChatSending(false);
    }
  }

  async function sendChatMessage() {
    const text = chatInput.trim();
    if (!text || chatSending || aiChatBlocked) return;
    setChatSending(true);
    onAuthoringChatBusyChange?.(selectedNode.id, true);
    setChatError(null); setChatErrorCode(null);
    setChatInput("");
    const localId = `local-${Date.now()}`;
    setChatMessages((prev) => [...prev, { id: localId, role: "user", content: text, created_at: new Date().toISOString() }]);
    try {
      const sid = await ensureAuthoringSession();
      const authoringModel = cfg.authoring_model_id?.trim();
      await apiFetch<{ assistantMessage: { id: string; displayText: string } }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/ai-provider/authoring/sessions/${encodeURIComponent(sid)}/messages`,
        {
          method: "POST",
          body: {
            message: text,
            currentPrompt: draftPrompt,
            currentSchema: JSON.stringify(cfg.output_schema ?? []),
            availableModels: tenantAiModels.map((m) => m.model_id),
            ...(authoringModel ? { model: authoringModel } : {})
          }
        }
      );
      await refreshChat();
    } catch (e) {
      if (isAiUsageLimitError(e)) {
        toast.push({ kind: "error", message: e.message });
        onAiChatRateLimited();
        setChatInput(text);
        setChatMessages((prev) => (prev.length && prev[prev.length - 1]?.id === localId ? prev.slice(0, -1) : prev));
      } else {
        setChatError(e instanceof Error ? e.message : String(e)); setChatErrorCode(aiRouteErrorCode(e));
        setChatInput(text);
        setChatMessages((prev) => (prev.length && prev[prev.length - 1]?.id === localId ? prev.slice(0, -1) : prev));
      }
    } finally {
      onAuthoringChatBusyChange?.(selectedNode.id, false);
      setChatSending(false);
    }
  }

  function commitPrompt() {
    if (mode === "production") return;
    if (draftPrompt === persistedPrompt) return;
    patchConfig({ prompt: draftPrompt });
  }

  function buildExecuteConfig(): Record<string, unknown> {
    const prompt = mode === "authoring" ? draftPrompt.trim() : (cfg.prompt ?? "").trim();
    return {
      ...(cfg as Record<string, unknown>),
      prompt
    };
  }

  const structuredEnabled = cfg.enable_structured !== false;
  const narrativeEnabled = cfg.enable_unstructured !== false;
  const promptOk = (mode === "authoring" ? draftPrompt.trim() : (cfg.prompt ?? "").trim()).length > 0;
  const schemaOk = !structuredEnabled || (cfg.output_schema?.length ?? 0) > 0;
  const outputModesOk = structuredEnabled || narrativeEnabled;
  const executeReady = promptOk && schemaOk && outputModesOk;

  async function executeAiProvider() {
    if (!executeReady || executeBusy) return;
    const nodeId = selectedNode.id;
    onNodeRunStatusChange(nodeId, "running");
    try {
      const data = await apiFetch<AiProviderExecuteResponse>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai-provider/execute`,
        { method: "POST", body: { config: buildExecuteConfig() } }
      );
      onNodeRunStatusChange(nodeId, "success");
      if (currentNodeIdRef.current === nodeId && data.run?.id) {
        setSelectedRunId(data.run.id);
        setSelectedRunDetail(data.run);
        setLastExecuteDebugRunId(data.run.id);
        setLastExecuteDebugRaw(typeof data.debug_raw_text === "string" ? data.debug_raw_text : null);
        setLastExecuteSubstitutedRunId(data.run.id);
        setLastExecuteSubstitutedFromModel(
          typeof data.substituted_from_model === "string" ? data.substituted_from_model : null
        );
        if (data.drift) {
          setLastExecuteDrift(data.drift);
          setLastExecuteDriftRunId(data.run.id);
        } else {
          setLastExecuteDrift(null);
          setLastExecuteDriftRunId(null);
        }
        setActiveSlot("runs");
      }
      toast.push({ kind: "success", message: "Execution finished." });
      await loadRuns();
    } catch (e) {
      onNodeRunStatusChange(nodeId, "error");
      if (currentNodeIdRef.current === nodeId) {
        if (e instanceof ApiRequestError) {
          const d = e.data as Record<string, unknown>;
          const runUnknown = d.run;
          if (runUnknown && typeof runUnknown === "object" && typeof (runUnknown as { id?: unknown }).id === "string") {
            const r = runUnknown as AiProviderRunDetail;
            setSelectedRunId(r.id);
            setSelectedRunDetail(r);
            setLastExecuteDebugRunId(r.id);
            setLastExecuteDebugRaw(typeof d.debug_raw_text === "string" ? d.debug_raw_text : null);
          }
        }
      }
      if (isAiUsageLimitError(e)) {
        toast.push({ kind: "error", message: e.message });
        onAiChatRateLimited();
      } else {
        toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      }
      await loadRuns();
    }
  }

  const outputSchemaSnapshot = JSON.stringify(cfg.output_schema ?? []);
  useEffect(() => {
    if (setupSubTab !== "schema") return;
    const base = cfg.output_schema ?? [];
    setSchemaRows(
      base.map((r) => ({
        name: r.name,
        type: coerceSchemaType(r.type),
        description: r.description ?? ""
      }))
    );
    setSchemaError(null);
  }, [setupSubTab, selectedNode.id, outputSchemaSnapshot]);

  function commitSchemaTableImpl(rows: AiProviderOutputColumn[]): boolean {
    if (mode === "production") return false;
    const saved = rows
      .map((r) => ({
        name: r.name.trim(),
        type: coerceSchemaType(r.type),
        description: (r.description ?? "").trim()
      }))
      .filter((r) => r.name.length > 0);
    const seen = new Set<string>();
    for (const r of saved) {
      if (seen.has(r.name)) {
        setSchemaError("Duplicate column name.");
        return false;
      }
      seen.add(r.name);
    }
    setSchemaError(null);
    patchConfig({
      output_schema: saved.map(({ name, type, description }) => ({
        name,
        type,
        ...(description ? { description } : {})
      }))
    });
    return true;
  }

  function applyAssistantProductionSuggestions(payload: {
    prompt: string | null;
    schema: AiProviderOutputColumn[] | null;
    executionModelId: string | null;
  }) {
    if (mode === "production") return;
    const applied: string[] = [];
    let schemaRejected = false;

    const p = payload.prompt?.trim() ?? "";
    if (p.length > 0) {
      setDraftPrompt(p);
      patchConfig({ prompt: p });
      applied.push("production prompt");
    }

    if (payload.schema && payload.schema.length > 0) {
      const rows = payload.schema.map((r) => ({
        name: r.name,
        type: coerceSchemaType(r.type),
        description: r.description ?? ""
      }));
      if (commitSchemaTableImpl(rows)) {
        setSchemaRows(rows);
        applied.push("structured output");
      } else {
        schemaRejected = true;
      }
    }

    const mid = payload.executionModelId?.trim() ?? "";
    if (mid.length > 0) {
      patchConfig({ execution_model_id: mid });
      applied.push("execution model");
    }

    if (schemaRejected) {
      toast.push({
        kind: "error",
        message:
          applied.length > 0
            ? "Structured output was not applied (duplicate column names). Other updates were saved."
            : "Structured output was not applied: duplicate column names."
      });
    }
    if (applied.length > 0) {
      toast.push({
        kind: "success",
        message: `Updated ${formatAppliedSuggestionParts(applied)}.`
      });
    }
  }

  const selectableModels = tenantAiModels.filter((m) => m.status === "active" || m.status === "deprecated");

  const accountDefault = accountDefaultAiModelId.trim();
  const authoringOverride = (cfg.authoring_model_id ?? "").trim();
  /** Shown when the node has no authoring override: account preference, else first model in the tenant list. */
  const effectiveAuthoringDefault = accountDefault || tenantAiModels[0]?.model_id || "";
  const authoringPillValue = authoringOverride || effectiveAuthoringDefault;
  const authoringOrphanOverride =
    authoringOverride && !tenantAiModels.some((m) => m.model_id === authoringOverride) ? authoringOverride : null;

  function handleAuthoringModelPillChange(next: string) {
    if (next === effectiveAuthoringDefault) {
      patchConfig({ authoring_model_id: undefined });
    } else {
      patchConfig({ authoring_model_id: next });
    }
  }

  const executionSelectId = `ai-provider-execution-model-${selectedNode.id}`;

  function beginResizeSchemaNameCol(e: ReactMouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startPct = schemaColWidths.namePct;
    const table = schemaTableRef.current;
    const tableWidth = Math.max(table?.getBoundingClientRect().width ?? 1, 1);
    function onMove(me: MouseEvent) {
      const dx = me.clientX - startX;
      const deltaPct = (dx / tableWidth) * 100;
      setSchemaColWidths((cw) => ({
        ...cw,
        namePct: Math.min(SCHEMA_COL_NAME_PCT.max, Math.max(SCHEMA_COL_NAME_PCT.min, startPct + deltaPct))
      }));
    }
    function onUp() {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  function beginResizeSchemaTypeCol(e: ReactMouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startPx = schemaColWidths.typePx;
    function onMove(me: MouseEvent) {
      const dx = me.clientX - startX;
      setSchemaColWidths((cw) => ({
        ...cw,
        typePx: Math.min(SCHEMA_COL_TYPE_PX.max, Math.max(SCHEMA_COL_TYPE_PX.min, startPx + dx))
      }));
    }
    function onUp() {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  return (
    <div className="opAiInspectorRoot" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Mode pill — node-global chrome above the tab rack. */}
      <div className="opAiInspectorTabBar" style={{ flexShrink: 0 }}>
        <span className={`pill opAiInspectorModePill ${mode === "production" ? "pillOk" : "pillWarn"}`}>
          {mode === "production" ? "Production" : "Authoring"}
        </span>
      </div>

      <InspectorTabs
        surfaceId="operationEditor"
        tabGroupId="ai_provider"
        activeSlot={activeSlot}
        onSlotChange={setActiveSlot}
        tabs={[
          {
            slot: "info",
            content: (
              <div style={{ display: "grid", gap: 8, padding: "4px 0" }}>
                <div className="field">
                  <label className="label">Node label</label>
                  <input
                    className="input"
                    value={selectedNode.data.label}
                    onChange={(e) => setNodeData(selectedNode.id, (d) => ({ ...d, label: e.target.value }))}
                    onBlur={() => queueAutoSave?.()}
                    placeholder="Unnamed"
                  />
                </div>
          <div className="field">
            <label className="label">Description</label>
            <textarea
              className="textarea"
              rows={3}
              placeholder="Optional description for this node…"
              value={selectedNode.data.description ?? ""}
              onChange={(e) => setNodeData(selectedNode.id, (d) => ({ ...d, description: e.target.value }))}
              onBlur={() => queueAutoSave?.()}
            />
          </div>
          <NodeMetadataFields meta={operationMeta} />
              </div>
            ),
          },
          ...(mode === "authoring"
            ? [{
                slot: "ai" as const,
                fill: true,
                content: (
                  <div className="opAiChatTab" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            {tenantAiModels.length > 0 || authoringOrphanOverride ? (
              <select
                className="pillSelect pillSelectNeutral"
                value={authoringPillValue}
                onChange={(e) => handleAuthoringModelPillChange(e.target.value)}
                disabled={chatSending || aiChatBlocked}
                title="Model for authoring chat"
              >
                {authoringOrphanOverride ? (
                  <option value={authoringOrphanOverride}>{authoringOrphanOverride} (not in tenant list)</option>
                ) : null}
                {tenantAiModels.map((m) => (
                  <option key={m.model_id} value={m.model_id}>
                    {m.model_id}
                    {m.status === "deprecated" ? " (deprecated)" : ""}
                  </option>
                ))}
              </select>
            ) : (
              <span />
            )}
            <button type="button" className="btn btnSm" onClick={() => void resetAuthoringChat()} disabled={chatSending || aiChatBlocked}>
              Reset chat
            </button>
          </div>
          {chatError ? (
            <div className="help" style={{ color: "var(--danger-text)" }}>
              {chatError}
            </div>
          ) : null}
          <div
            ref={chatScrollRef}
            className="card opAiChatScroll"
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              background: "var(--bg-subtle)"
            }}
          >
            <div className="opAiChatThread">
              {chatMessages.map((m) =>
                m.role === "user" ? (
                  <div key={m.id} className="opAiChatRow opAiChatRowUser">
                    <div className="opAiChatMeta">You</div>
                    <div className="opAiChatBubbleUser">
                      <div className="opAiChatProse">{m.content}</div>
                    </div>
                  </div>
                ) : (
                  <div key={m.id} className="opAiChatRow opAiChatRowAssistant">
                    <div className="opAiChatMeta">Assistant</div>
                    <div className="opAiChatBubbleAssistant">
                      <AssistantTurn
                        conversationText={m.content}
                        suggestedPrompt={m.suggestedPrompt}
                        suggestedSchema={m.suggestedSchema}
                        suggestedExecutionModelId={m.suggestedSettings?.suggested_execution_model_id ?? null}
                        tenantAiModelIds={tenantAiModelIds}
                        onApplyProductionSuggestions={applyAssistantProductionSuggestions}
                        mode={mode}
                      />
                    </div>
                  </div>
                )
              )}
              <ChatThinkingIndicator visible={replyPending} />
            </div>
          </div>
          <ChatErrorBanner message={chatError ?? chatThreadError} code={chatError ? chatErrorCode : null} />
          <ChatComposer
            draft={chatInput}
            setDraft={setChatInput}
            onSend={() => void sendChatMessage()}
            onCancel={() => void cancelChat()}
            inFlight={chatSending || replyPending}
            placeholder="Describe what you want this AI Provider to do…"
            disabledExtra={aiChatBlocked}
            disabledExtraTitle="AI chat temporarily limited — try again in about a minute."
            minRows={2}
            maxRows={12}
          />
          {aiChatBlocked ? (
            <div className="help">AI chat temporarily limited — try again in about a minute.</div>
          ) : null}
                  </div>
                ),
              }]
            : []),
          {
            slot: "setup",
            content: (
              <div style={{ display: "grid", gap: 10 }}>
                <div className="reportTabs" role="tablist" aria-label="AI provider setup sections">
                  <button className={`reportTabBtn${setupSubTab === "config" ? " reportTabBtnActive" : ""}`} type="button" onClick={() => setSetupSubTab("config")}>Configuration</button>
                  <button className={`reportTabBtn${setupSubTab === "schema" ? " reportTabBtnActive" : ""}`} type="button" onClick={() => setSetupSubTab("schema")}>Schema</button>
                </div>
                {setupSubTab === "config" ? (
                  <div className="opAiProviderConfig">

          {/* Context inputs status — read-only indicator, not a configurable setting */}
          {(contextInputs.dataIn || contextInputs.contextIn) ? (
            <div className="opAiContextStatus">
              {contextInputs.dataIn ? (
                <span className="opAiContextPill opAiContextPillTabular">Tabular data connected</span>
              ) : null}
              {contextInputs.contextIn ? (
                <span className="opAiContextPill opAiContextPillContent">Content connected</span>
              ) : null}
            </div>
          ) : null}

          {/* Production prompt */}
          <div className="field opAiProviderConfigPrompt">
            <label className="label" htmlFor={`ai-provider-prompt-${selectedNode.id}`}>
              Production prompt
            </label>
            <textarea
              id={`ai-provider-prompt-${selectedNode.id}`}
              className="input"
              rows={6}
              value={draftPrompt}
              disabled={mode === "production"}
              onChange={(e) => setDraftPrompt(e.target.value)}
              onBlur={() => commitPrompt()}
            />
          </div>

          {/* ── Execution model ───────────────────── */}
          <div className="opAiConfigSection">
            <div className="field">
              <label className="label" htmlFor={executionSelectId}>
                Execution model
              </label>
              <select
                id={executionSelectId}
                className="select"
                value={cfg.execution_model_id ?? ""}
                onChange={(e) => patchConfig({ execution_model_id: e.target.value || undefined })}
              >
                <option value="">Account default</option>
                {selectableModels.map((m) => (
                  <option key={m.model_id} value={m.model_id}>
                    {m.model_id}
                    {m.status === "deprecated" ? " (deprecated)" : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Search ────────────────────────────── */}
          <div className="opAiConfigSection">
            <div className="opAiConfigSectionHeader">Search</div>
            <div className="opAiCheckboxGroup">
              <label className="opAiCheckboxRow" style={{ cursor: mode === "production" ? "default" : "pointer" }}>
                <input
                  type="checkbox"
                  checked={webSearchEnabled}
                  disabled={mode === "production"}
                  onChange={(e) => {
                    const on = e.target.checked;
                    const cur = (cfg.search_config ?? {}) as Record<string, unknown>;
                    patchConfig({
                      search_config: {
                        ...cur,
                        web_search_enabled: on,
                        max_search_passes: (cur.max_search_passes as 1 | 2 | 3 | undefined) ?? 2,
                        max_results_per_search: (cur.max_results_per_search as number | undefined) ?? 5
                      }
                    });
                  }}
                />
                <span>Web search enabled</span>
              </label>
              {webSearchEnabled ? (
                <label className="opAiCheckboxRow" style={{ cursor: mode === "production" ? "default" : "pointer" }}>
                  <input
                    type="checkbox"
                    checked={cfg.citations_required !== false}
                    disabled={mode === "production"}
                    onChange={(e) => patchConfig({ citations_required: e.target.checked })}
                  />
                  <span>Citations required</span>
                </label>
              ) : null}
            </div>
            {webSearchEnabled ? (
              <div className="opAiConfigTwoCol">
                <div className="field">
                  <label className="label" htmlFor={`ai-provider-max-passes-${selectedNode.id}`}>
                    Max search passes
                  </label>
                  <select
                    id={`ai-provider-max-passes-${selectedNode.id}`}
                    className="select"
                    disabled={mode === "production"}
                    value={String(cfg.search_config?.max_search_passes ?? 2)}
                    onChange={(e) => {
                      const n = Number(e.target.value) as 1 | 2 | 3;
                      patchSearchConfig({ max_search_passes: n === 1 || n === 2 || n === 3 ? n : 2 });
                    }}
                  >
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                  </select>
                </div>
                <div className="field">
                  <label className="label" htmlFor={`ai-provider-max-results-${selectedNode.id}`}>
                    Max results per search
                  </label>
                  <input
                    id={`ai-provider-max-results-${selectedNode.id}`}
                    className="input"
                    type="number"
                    min={1}
                    max={50}
                    step={1}
                    disabled={mode === "production"}
                    value={cfg.search_config?.max_results_per_search ?? 5}
                    onChange={(e) => {
                      const v = e.target.valueAsNumber;
                      if (Number.isNaN(v)) return;
                      patchSearchConfig({ max_results_per_search: Math.min(50, Math.max(1, Math.round(v))) });
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>

          {/* ── Output ────────────────────────────── */}
          <div className="opAiConfigSection">
            <div className="opAiConfigSectionHeader">Output</div>
            <div className="opAiCheckboxGroup" style={{ marginBottom: 10 }}>
              <label className="opAiCheckboxRow" style={{ cursor: mode === "production" ? "default" : "pointer" }}>
                <input
                  type="checkbox"
                  checked={structuredEnabled}
                  disabled={mode === "production"}
                  onChange={(e) => {
                    const on = e.target.checked;
                    if (!on && !narrativeEnabled) return;
                    patchConfig({ enable_structured: on });
                  }}
                />
                <span>Structured output (tabular)</span>
              </label>
              <label className="opAiCheckboxRow" style={{ cursor: mode === "production" ? "default" : "pointer" }}>
                <input
                  type="checkbox"
                  checked={narrativeEnabled}
                  disabled={mode === "production"}
                  onChange={(e) => {
                    const on = e.target.checked;
                    if (!on && !structuredEnabled) return;
                    patchConfig({ enable_unstructured: on });
                  }}
                />
                <span>Narrative output (content)</span>
              </label>
            </div>
            <div className="opAiConfigTwoCol">
              <div className="field">
                <label className="label" htmlFor={`ai-provider-temperature-${selectedNode.id}`}>
                  Temperature
                </label>
                <input
                  id={`ai-provider-temperature-${selectedNode.id}`}
                  className="input"
                  type="number"
                  min={0}
                  max={2}
                  step={0.1}
                  value={cfg.model_tuning?.temperature ?? 0.1}
                  onChange={(e) => {
                    const v = e.target.valueAsNumber;
                    if (Number.isNaN(v)) return;
                    patchModelTuning({ temperature: Math.round(Math.min(2, Math.max(0, v)) * 10) / 10 });
                  }}
                />
              </div>
              <div className="field">
                <label className="label" htmlFor={`ai-provider-max-tokens-${selectedNode.id}`}>
                  Max output tokens
                </label>
                <input
                  id={`ai-provider-max-tokens-${selectedNode.id}`}
                  className="input"
                  type="number"
                  min={1}
                  max={128000}
                  step={1}
                  value={cfg.model_tuning?.max_output_tokens ?? 4000}
                  onChange={(e) => {
                    const v = e.target.valueAsNumber;
                    if (Number.isNaN(v)) return;
                    patchModelTuning({ max_output_tokens: Math.min(128000, Math.max(1, Math.round(v))) });
                  }}
                />
              </div>
              {narrativeEnabled ? (
                <div className="field">
                  <label className="label" htmlFor={`ai-provider-content-format-${selectedNode.id}`}>
                    Content format
                  </label>
                  <select
                    id={`ai-provider-content-format-${selectedNode.id}`}
                    className="select"
                    value={cfg.content_format ?? "markdown"}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "markdown" || v === "svg" || v === "html") {
                        patchConfig({ content_format: v });
                      }
                    }}
                  >
                    <option value="markdown">Markdown</option>
                    <option value="svg">SVG</option>
                    <option value="html">HTML</option>
                  </select>
                </div>
              ) : null}
            </div>
          </div>

          {/* ── Actions ───────────────────────────── */}
          <div className="opAiConfigActions">
            <button
              type="button"
              className="btn btnSm btnPrimary"
              disabled={!executeReady || executeBusy}
              onClick={() => void executeAiProvider()}
            >
              {executeBusy ? "Running…" : "Execute"}
            </button>
            {mode === "authoring" ? (
              <button
                type="button"
                className="btn btnSm"
                onClick={() => {
                  patchConfig({
                    mode: "production",
                    locked_at: new Date().toISOString(),
                    prompt: draftPrompt
                  });
                }}
              >
                Lock for Production
              </button>
            ) : (
              <button
                type="button"
                className="btn btnSm btnTertiary"
                onClick={() => {
                  patchConfig({
                    mode: "authoring",
                    locked_at: undefined
                  });
                }}
              >
                Return to authoring
              </button>
            )}
            {mode === "production" && cfg.locked_at ? (
              <span className="muted opAiLockedAt">Locked {formatUtcISOInTz(cfg.locked_at, tenantTimezone)}</span>
            ) : null}
          </div>

                  </div>
                ) : null}
                {setupSubTab === "schema" ? (
                  <div className="opAiSchemaEditor">
          {schemaError ? (
            <div className="help" style={{ color: "var(--danger-text)" }}>
              {schemaError}
            </div>
          ) : null}
          {mode === "authoring" ? (
            <div className="opAiSchemaToolbar">
              <button
                type="button"
                className="btn btnSm"
                onClick={() => {
                  setSchemaRows((prev) => [...prev, { name: "", type: "string", description: "" }]);
                }}
              >
                Add column
              </button>
            </div>
          ) : null}
          <div className="tableWrap">
            <table ref={schemaTableRef} className="table tableCompact opAiSchemaTable">
              <colgroup>
                <col style={{ width: `${schemaColWidths.namePct}%` }} />
                <col style={{ width: `${schemaColWidths.typePx}px` }} />
                <col />
                {mode === "authoring" ? <col style={{ width: 30 }} /> : null}
              </colgroup>
              <thead>
                <tr>
                  <th className="opAiSchemaTh">
                    Name
                    <span
                      className="opAiSchemaColResize"
                      role="separator"
                      aria-orientation="vertical"
                      aria-label="Resize name column"
                      onMouseDown={beginResizeSchemaNameCol}
                    />
                  </th>
                  <th className="opAiSchemaTh">
                    Type
                    <span
                      className="opAiSchemaColResize"
                      role="separator"
                      aria-orientation="vertical"
                      aria-label="Resize type column"
                      onMouseDown={beginResizeSchemaTypeCol}
                    />
                  </th>
                  <th>Description</th>
                  {mode === "authoring" ? <th aria-label="Remove" /> : null}
                </tr>
              </thead>
              <tbody>
                {schemaRows.length === 0
                  ? null
                  : schemaRows.map((row, i) => (
                      <tr key={i}>
                        <td>
                          <input
                            className="opAiSchemaInput mono"
                            value={row.name}
                            readOnly={mode === "production"}
                            autoComplete="off"
                            onChange={(e) => {
                              const name = e.target.value;
                              setSchemaRows((prev) => prev.map((r, j) => (j === i ? { ...r, name } : r)));
                            }}
                            onBlur={() => {
                              setSchemaRows((current) => {
                                commitSchemaTableImpl(current);
                                return current;
                              });
                            }}
                          />
                        </td>
                        <td>
                          <select
                            className="opAiSchemaSelect"
                            value={row.type}
                            disabled={mode === "production"}
                            aria-label="Type"
                            onChange={(e) => {
                              const type = coerceSchemaType(e.target.value);
                              setSchemaRows((prev) => {
                                const next = prev.map((r, j) => (j === i ? { ...r, type } : r));
                                commitSchemaTableImpl(next);
                                return next;
                              });
                            }}
                          >
                            {SCHEMA_COLUMN_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            className="opAiSchemaInput"
                            value={row.description ?? ""}
                            readOnly={mode === "production"}
                            autoComplete="off"
                            onChange={(e) => {
                              const description = e.target.value;
                              setSchemaRows((prev) => prev.map((r, j) => (j === i ? { ...r, description } : r)));
                            }}
                            onBlur={() => {
                              setSchemaRows((current) => {
                                commitSchemaTableImpl(current);
                                return current;
                              });
                            }}
                          />
                        </td>
                        {mode === "authoring" ? (
                          <td>
                            <button
                              type="button"
                              className="btn btnSm btnTertiary opAiSchemaRowDel"
                              aria-label="Remove column"
                              onClick={() => {
                                setSchemaRows((prev) => {
                                  const next = prev.filter((_, j) => j !== i);
                                  commitSchemaTableImpl(next);
                                  return next;
                                });
                              }}
                            >
                              ×
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
                  </div>
                ) : null}
              </div>
            ),
          },
          {
            slot: "runs",
            content: (
              <div className="opAiHistoryTab">
          {runsLoading ? <div className="muted">Loading runs…</div> : null}
          {runsError ? (
            <div className="help" style={{ color: "var(--danger-text)" }}>
              {runsError}
            </div>
          ) : null}
          {!runsLoading && runs.length === 0 ? (
            <div className="muted" style={{ fontSize: 13 }}>
              No execution runs yet. Use Execute on the Configuration tab to run research.
            </div>
          ) : null}
          {runs.length > 0 ? (
            <>
              <div className="field">
                <label className="label" htmlFor={`ai-provider-run-select-${selectedNode.id}`}>
                  Execution
                </label>
                <select
                  id={`ai-provider-run-select-${selectedNode.id}`}
                  className="select"
                  value={selectedRunId ?? ""}
                  onChange={(e) => setSelectedRunId(e.target.value || null)}
                >
                  {runs.map((r) => (
                    <option key={r.id} value={r.id}>
                      {formatUtcISOInTz(r.created_at, tenantTimezone)} — {r.status}
                      {r.is_test ? " (test)" : ""}
                      {r.model_used ? ` — ${r.model_used}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              {olderRunDetailLoading ? <div className="muted" style={{ fontSize: 12 }}>Loading prior run for drift…</div> : null}
              {activeStructuredDrift ? (
                <div className="card" style={{ padding: 10, marginTop: 4, background: "var(--bg-subtle)" }}>
                  <div className="label" style={{ marginBottom: 6 }}>
                    Structured output drift vs previous production run
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                    Changed cells {activeStructuredDrift.changed_cells} / {activeStructuredDrift.total_cells}; added rows{" "}
                    {activeStructuredDrift.added_rows}; removed rows {activeStructuredDrift.removed_rows}.
                  </div>
                  {activeStructuredDrift.changes.length > 0 ? (
                    <div className="tanTableWrap" style={{ maxHeight: 200, overflow: "auto" }}>
                      <table className="tanTable" style={{ fontSize: 12 }}>
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>Column</th>
                            <th>Before</th>
                            <th>After</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeStructuredDrift.changes.slice(0, 80).map((c, i) => (
                            <tr key={`${c.row}-${c.column}-${i}`}>
                              <td>{c.row}</td>
                              <td className="mono">{c.column}</td>
                              <td className="mono" style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {JSON.stringify(c.old_value)}
                              </td>
                              <td className="mono" style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {JSON.stringify(c.new_value)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                  {activeStructuredDrift.changes.length > 80 ? (
                    <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Showing first 80 cell changes.</div>
                  ) : null}
                </div>
              ) : null}
              {runDetailLoading ? <div className="muted">Loading run…</div> : null}
              {!runDetailLoading && selectedRunDetail ? (
                <>
                  {selectedRunDetail.error_message && selectedRunDetail.status !== "success" ? (
                    <div className="help" style={{ color: "var(--danger-text)" }}>
                      {selectedRunDetail.error_message}
                    </div>
                  ) : null}
                  {/* ADR-0059: surface when Hub routed web search to a substitute model */}
                  {selectedRunId === lastExecuteSubstitutedRunId && lastExecuteSubstitutedFromModel ? (
                    <div className="pill pillNeutral" style={{ marginBottom: 6, fontSize: 12 }}>
                      Web search routed to <span className="mono">{selectedRunDetail.model_used ?? "web model"}</span> — <span className="mono">{lastExecuteSubstitutedFromModel}</span> can't do web search.
                    </div>
                  ) : null}
                  <div className="tabs opAiHistoryResultTabs" role="tablist" aria-label="Run result">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={historyResultTab === "structured"}
                      className={`tab ${historyResultTab === "structured" ? "tabActive" : ""}`}
                      onClick={() => setHistoryResultTab("structured")}
                    >
                      Structured
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={historyResultTab === "unstructured"}
                      className={`tab ${historyResultTab === "unstructured" ? "tabActive" : ""}`}
                      onClick={() => setHistoryResultTab("unstructured")}
                    >
                      Unstructured
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={historyResultTab === "citations"}
                      className={`tab ${historyResultTab === "citations" ? "tabActive" : ""}`}
                      onClick={() => setHistoryResultTab("citations")}
                    >
                      Citations
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={historyResultTab === "debug"}
                      className={`tab ${historyResultTab === "debug" ? "tabActive" : ""}`}
                      onClick={() => setHistoryResultTab("debug")}
                    >
                      Debug
                    </button>
                  </div>
                  <div className="opAiHistoryResultBody">
                    {historyResultTab === "structured" ? (
                      structuredRunTanModel ? (
                        <div className="tanTableWrap opAiHistoryTanWrap">
                          <TanTableViz model={structuredRunTanModel} showExport={false} />
                        </div>
                      ) : (
                        <div className="muted" style={{ fontSize: 13 }}>
                          {selectedRunDetail.status === "success"
                            ? "No structured output for this run."
                            : "Structured output unavailable for this run."}
                        </div>
                      )
                    ) : null}
                    {historyResultTab === "unstructured" ? (
                      (() => {
                        const snap = parseContentSnapshot(selectedRunDetail.content_snapshot);
                        if (!snap || !snap.body.trim()) {
                          return (
                            <div className="muted" style={{ fontSize: 13 }}>
                              No unstructured content for this run.
                            </div>
                          );
                        }
                        if (snap.contentType === "html") {
                          return (
                            <iframe
                              title="Unstructured HTML"
                              className="opAiHistoryContentFrame"
                              sandbox="allow-same-origin"
                              srcDoc={snap.body}
                            />
                          );
                        }
                        if (snap.contentType === "svg") {
                          return (
                            <div
                              className="opAiHistorySvgHost"
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{ __html: snap.body }}
                            />
                          );
                        }
                        return (
                          <div className="opAiHistoryMarkdown">
                            {snap.title ? <div className="opAiHistoryContentTitle">{snap.title}</div> : null}
                            <ReactMarkdown>{snap.body}</ReactMarkdown>
                          </div>
                        );
                      })()
                    ) : null}
                    {historyResultTab === "citations" ? (
                      citationsRunTanModel ? (
                        <div className="tanTableWrap opAiHistoryTanWrap">
                          <TanTableViz model={citationsRunTanModel} showExport={false} />
                        </div>
                      ) : (
                        <div className="muted" style={{ fontSize: 13 }}>
                          No citations for this run.
                        </div>
                      )
                    ) : null}
                    {historyResultTab === "debug" ? (
                      selectedRunId === lastExecuteDebugRunId && lastExecuteDebugRaw !== null ? (
                        <textarea
                          readOnly
                          className="opAiHistoryDebugRaw mono"
                          value={lastExecuteDebugRaw}
                          spellCheck={false}
                          aria-label="Raw model text from last Execute"
                        />
                      ) : (
                        <div className="muted" style={{ fontSize: 13 }}>
                          Raw model text is kept in memory only for the run from your latest Execute in this inspector.
                          Select that run or execute again to see it here.
                        </div>
                      )
                    ) : null}
                  </div>
                </>
              ) : null}
            </>
          ) : null}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
